import {
  faArrowRotateLeft,
  faArrowRotateRight,
  faFileAudio,
  faFileVideo,
  faPause,
  faPlay,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import type { Props } from "web-ui/dist/types/components/LegacyButton";
import { LegacyButton as Button } from "web-ui/legacy";

import { getAudioMp3 } from "../api/generalTts";
import styles from "../styles/Button.module.scss";
import { downloadData } from "../util/download";

import { useAnimationDownloader } from "./useAnimationDownloader";

interface AudioControlButtonsProps extends Props {
  source: string;
  onAudioPause?: () => void;
  onAudioPlay?: (time: number) => void;
  onAudioScrub?: (time: number) => void;
  audioRef: React.MutableRefObject<HTMLAudioElement | undefined>;
  canvas?: HTMLCanvasElement | null;
}

/**
 * AudioControlButtons is used to create and control audio.
 *
 * @param props is described below. Additional props are passed directly to the child `web-ui.Button` component.
 * @param props.audioRef is a reference to the audio object created by this component, and can be used to view or change the audio's state.
 * @param props.canvas is the canvas object that the Rive engine uses to display the animation
 * @param props.source is the url of the audio to play
 * @param props.onAudioPlay is called when the audio is played. The parameter is the current timestamp of the audio when it is played.
 * @param props.onAudioPause is called when the audio is paused.
 * @param props.onAudioScrub is called when the audio's playback time is manually changed by one of the buttons. The parameter is the new timestamp of the audio.
 */
export const AudioControlButtons = ({
  audioRef,
  canvas,
  source,
  onAudioPause,
  onAudioPlay,
  onAudioScrub,
  ...props
}: AudioControlButtonsProps) => {
  const [isPlaying, setIsPlaying] = React.useState(false);

  const { isRecording, startRecording, stopAndDownloadRecording } =
    useAnimationDownloader(canvas, source);

  React.useEffect(() => {
    audioRef.current = new Audio(source);
    setIsPlaying(false);

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    };
  }, [audioRef, source, canvas]);

  React.useEffect(() => {
    if (audioRef.current) {
      audioRef.current.onended = () => {
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          setIsPlaying(false);
          onAudioPause?.();
          onAudioScrub?.(0);
          if (isRecording()) {
            stopAndDownloadRecording();
          }
        }
      };
    }
  }, [
    audioRef,
    isRecording,
    onAudioPause,
    onAudioScrub,
    stopAndDownloadRecording,
  ]);

  return (
    <div className={styles["button-container"]}>
      <Button
        className={styles["audio-play-button"]}
        {...props}
        onClick={() => {
          if (audioRef.current) {
            audioRef.current.currentTime -= 1;
            onAudioScrub?.(audioRef.current.currentTime);
          }
        }}
      >
        <FontAwesomeIcon icon={faArrowRotateLeft} />
      </Button>
      <Button
        className={styles["audio-play-button"]}
        {...props}
        onClick={async () => {
          if (audioRef.current) {
            if (isPlaying) {
              audioRef.current.pause();
              setIsPlaying(false);
              onAudioPause?.();
              // If manually paused and we don't manually stop recording,
              // the recording will run forever.
              if (isRecording()) {
                stopAndDownloadRecording();
              }
            } else {
              await audioRef.current.play();
              setIsPlaying(true);
              onAudioPlay?.(audioRef.current.currentTime);
            }
          }
        }}
      >
        {isPlaying ? (
          <FontAwesomeIcon icon={faPause} />
        ) : (
          <FontAwesomeIcon icon={faPlay} />
        )}
      </Button>
      <Button
        className={styles["audio-play-button"]}
        {...props}
        onClick={() => {
          if (audioRef.current) {
            audioRef.current.currentTime += 1;
            onAudioScrub?.(audioRef.current.currentTime);
          }
        }}
      >
        <FontAwesomeIcon icon={faArrowRotateRight} />
      </Button>
      <Button
        className={styles["audio-play-button"]}
        {...props}
        onClick={async () => {
          if (audioRef?.current?.src) {
            // Downloads are not CORS aware but rather require same-origin,
            // so rather than link to the MP3 file on Cloudfront with the
            // a tag having a download attribute, we must manually download the
            // content.
            const mp3 = await getAudioMp3(audioRef.current.src);
            downloadData(
              mp3,
              audioRef?.current?.src.split("/").at(-1) ?? "audio.mp3",
            );
          }
        }}
      >
        <FontAwesomeIcon icon={faFileAudio} />
      </Button>
      {canvas ? (
        <Button
          className={styles["audio-play-button"]}
          onClick={async () => {
            if (audioRef.current) {
              audioRef.current.pause();
              setIsPlaying(false);
              onAudioPause?.();
              audioRef.current.currentTime = 0;
              onAudioScrub?.(audioRef.current.currentTime);
              await startRecording();
              await audioRef.current.play();
              setIsPlaying(true);
              onAudioPlay?.(audioRef.current.currentTime);
            }
          }}
          variant="solid"
          {...props}
        >
          <FontAwesomeIcon icon={faFileVideo} />
        </Button>
      ) : null}
    </div>
  );
};
